import * as echarts from 'echarts';

import {
  CHART_SERIES_DARK, CHART_THEME_NAME, CHART_THEME_TOKENS, ChartThemeTokens, ResolvedTheme,
} from 'constants/theme';

const FONT = '"Source Sans Pro", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

function axis(tokens: ChartThemeTokens) {
  return {
    axisLine: { lineStyle: { color: tokens.axis } },
    axisTick: { lineStyle: { color: tokens.axis } },
    axisLabel: { color: tokens.text },
    splitLine: { lineStyle: { color: [tokens.grid] } },
    splitArea: { areaStyle: { color: ['transparent'] } },
    nameTextStyle: { color: tokens.text },
  };
}

/**
 * Tema ECharts com o CHROME do gráfico (texto, eixos, grade, tooltip, legenda). A paleta
 * das séries fica de fora de propósito: cor de dado não muda com o tema.
 */
export function buildChartTheme(tokens: ChartThemeTokens) {
  return {
    backgroundColor: 'transparent',
    textStyle: { color: tokens.text, fontFamily: FONT },
    title: { textStyle: { color: tokens.text }, subtextStyle: { color: tokens.text } },
    legend: {
      textStyle: { color: tokens.text },
      inactiveColor: tokens.axis,
      pageTextStyle: { color: tokens.text },
      pageIconColor: tokens.text,
      pageIconInactiveColor: tokens.axis,
    },
    tooltip: {
      backgroundColor: tokens.tooltipBg,
      borderColor: tokens.tooltipBorder,
      textStyle: { color: tokens.tooltipText },
      axisPointer: { lineStyle: { color: tokens.axis }, crossStyle: { color: tokens.axis } },
    },
    categoryAxis: axis(tokens),
    valueAxis: axis(tokens),
    timeAxis: axis(tokens),
    logAxis: axis(tokens),
    dataZoom: {
      textStyle: { color: tokens.text },
      borderColor: tokens.grid,
      dataBackground: { lineStyle: { color: tokens.axis }, areaStyle: { color: tokens.grid } },
    },
    visualMap: { textStyle: { color: tokens.text } },
    toolbox: { iconStyle: { borderColor: tokens.text } },
    markPoint: { label: { color: tokens.tooltipText } },
  };
}

function normalizeHex(color: string): string | null {
  const m = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!m) return null;
  const hex = m[1].length === 3 ? m[1].split('').map((c) => c + c).join('') : m[1];
  return `#${hex.toUpperCase()}`;
}

/** Cor de série legível no tema: no escuro troca as cores da marca/neutros (CHART_SERIES_DARK). */
export function adaptSeriesColor(color: string, theme: ResolvedTheme): string {
  if (theme !== 'dark') return color;
  const hex = normalizeHex(color);
  return (hex && CHART_SERIES_DARK[hex]) || color;
}

// Chaves de CHROME (texto, eixo, grade, tooltip) que o tema já pinta: no escuro, um valor fixo
// vindo do `option` (montado aqui ou pelo backend) é descartado para o tema valer.
const CHROME_PATHS: string[][] = [
  ['axisLabel', 'color'], ['nameTextStyle', 'color'], ['axisLine', 'lineStyle', 'color'],
  ['axisTick', 'lineStyle', 'color'], ['splitLine', 'lineStyle', 'color'],
];
const AXIS_KEYS = ['xAxis', 'yAxis', 'singleAxis', 'radiusAxis', 'angleAxis'];
const BRANCOS = new Set(['#FFF', '#FFFFFF', 'WHITE']);

type Obj = Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any

const isObj = (v: unknown): v is Obj => typeof v === 'object' && v !== null && !Array.isArray(v);

// Cópia profunda de objetos/arrays que mantém funções (formatters) pela mesma referência —
// o echarts-for-react compara o `option` por igualdade profunda.
function clone<T>(v: T): T {
  if (Array.isArray(v)) return v.map(clone) as unknown as T;
  if (isObj(v)) return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, clone(x)])) as T;
  return v;
}

function dropPath(o: Obj, path: string[]): void {
  let atual: Obj | undefined = o;
  for (let i = 0; i < path.length - 1; i += 1) {
    atual = isObj(atual?.[path[i]]) ? atual?.[path[i]] : undefined;
    if (!atual) return;
  }
  delete atual[path[path.length - 1]];
}

const asList = (v: unknown): Obj[] => (Array.isArray(v) ? v : [v]).filter(isObj);

function adaptColors(o: Obj, theme: ResolvedTheme, tokens: ChartThemeTokens): void {
  Object.keys(o).forEach((k) => {
    const v = o[k];
    if (typeof v === 'string' && /color$/i.test(k)) {
      if (k === 'backgroundColor' && BRANCOS.has(v.toUpperCase())) o[k] = tokens.tooltipBg; // eslint-disable-line no-param-reassign
      else o[k] = adaptSeriesColor(v, theme); // eslint-disable-line no-param-reassign
    } else if (Array.isArray(v) && k === 'color') {
      o[k] = v.map((c) => (typeof c === 'string' ? adaptSeriesColor(c, theme) : c)); // eslint-disable-line no-param-reassign
    } else if (isObj(v) || Array.isArray(v)) {
      asList(v).forEach((filho) => adaptColors(filho, theme, tokens));
    }
  });
}

/**
 * `option` pronto para o tema: no claro devolve o próprio objeto; no escuro devolve uma cópia
 * com as cores de série adaptadas (CHART_SERIES_DARK), fundo branco de rótulo trocado pela
 * superfície e as cores fixas de eixo/legenda/título/tooltip removidas (o tema registrado pinta).
 */
export function adaptChartOption<T>(option: T, theme: ResolvedTheme): T {
  if (theme !== 'dark' || !isObj(option)) return option;
  const o = clone(option) as Obj;
  AXIS_KEYS.forEach((eixo) => asList(o[eixo]).forEach((ax) => {
    CHROME_PATHS.forEach((p) => dropPath(ax, p));
  }));
  asList(o.legend).forEach((l) => dropPath(l, ['textStyle', 'color']));
  asList(o.title).forEach((t) => {
    dropPath(t, ['textStyle', 'color']);
    dropPath(t, ['subtextStyle', 'color']);
  });
  asList(o.tooltip).forEach((t) => {
    delete t.backgroundColor; // eslint-disable-line no-param-reassign
    delete t.borderColor; // eslint-disable-line no-param-reassign
    dropPath(t, ['textStyle', 'color']);
  });
  if (typeof o.backgroundColor === 'string') delete o.backgroundColor;
  const tokens = CHART_THEME_TOKENS[theme];
  if (Array.isArray(o.color)) {
    o.color = o.color.map((c: unknown) => (typeof c === 'string' ? adaptSeriesColor(c, theme) : c));
  }
  // Série, legenda e régua de zoom: cores da marca adaptadas, fundo branco vira superfície.
  [...asList(o.series), ...asList(o.legend), ...asList(o.dataZoom)]
    .forEach((item) => adaptColors(item, theme, tokens));
  asList(o.visualMap).forEach((vm) => dropPath(vm, ['textStyle', 'color']));
  return o as T;
}

let registered = false;

/** Registra `ee-light` e `ee-dark` uma única vez (idempotente). */
export function registerChartThemes(): void {
  if (registered) return;
  (Object.keys(CHART_THEME_NAME) as ResolvedTheme[]).forEach((theme) => {
    echarts.registerTheme(CHART_THEME_NAME[theme], buildChartTheme(CHART_THEME_TOKENS[theme]));
  });
  registered = true;
}
